import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowUpRight, Sparkles } from "lucide-react";
import { api } from "@/lib/apiClient";
import { trackPurchase } from "@/lib/tracking";
import { log } from "@/lib/log";

const MAX_POLLS = 8;

export default function Merci() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const product = searchParams.get("product");
  const [state, setState] = useState(sessionId ? "checking" : "success");
  const [details, setDetails] = useState(null);
  const firedRef = useRef(false);

  useEffect(() => {
    // Cas gratuit (lead) : conversion immédiate
    if (!sessionId) {
      if (!firedRef.current) {
        firedRef.current = true;
        trackPurchase({ value: 0, currency: "EUR", packageId: product || "discovery_pack", transactionId: `lead_${Date.now()}` });
      }
      return;
    }
    // Cas payant : polling du statut
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setDetails(data);
          setState("success");
          if (!firedRef.current) {
            firedRef.current = true;
            trackPurchase({
              value: data.amount || 0,
              currency: (data.currency || "eur").toUpperCase(),
              transactionId: sessionId,
              packageId: data.product_name,
            });
          }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        log.warn("[merci] polling statut paiement — nouvelle tentative :", e);
      }
      polls += 1;
      if (polls < MAX_POLLS) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, product]);

  return (
    <div className="min-h-screen flex items-center justify-center pt-28 pb-24 px-6" data-testid="merci-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
        className="max-w-2xl text-center"
      >
        {state === "checking" && (
          <div data-testid="merci-checking">
            <Loader2 className="animate-spin text-gold mx-auto mb-6" size={40} strokeWidth={1.5} />
            <h1 className="font-serif text-3xl text-white">Confirmation de votre paiement…</h1>
            <p className="text-white/50 mt-3">Merci de patienter quelques instants.</p>
          </div>
        )}

        {(state === "success" || state === "pending") && (
          <div data-testid="merci-success">
            <div className="w-20 h-20 mx-auto mb-8 border border-gold flex items-center justify-center">
              {state === "success" ? <Check className="text-gold" size={36} strokeWidth={1.5} /> : <Sparkles className="text-gold" size={32} strokeWidth={1.5} />}
            </div>
            <p className="text-xs uppercase tracking-luxe text-gold mb-6">Bienvenue dans le Cercle</p>
            <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight mb-6">
              Merci. Votre voyage commence.
            </h1>
            <p className="text-white/60 font-light text-lg leading-relaxed">
              {sessionId
                ? state === "success"
                  ? "Votre paiement est confirmé. Vous recevrez sous peu un email avec les prochaines étapes de votre accompagnement."
                  : "Votre commande est en cours de traitement. Vous recevrez une confirmation par email."
                : "Merci ! Un email de confirmation vous a été envoyé. Veuillez cliquer sur le lien dans cet email pour valider votre adresse et recevoir votre diagnostic personnalisé. Pensez à vérifier vos spams."}
            </p>
            {details && (
              <p className="mt-4 text-white/40 text-sm">
                {details.product_name} — {details.amount} {(details.currency || "eur").toUpperCase()}
              </p>
            )}
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" data-testid="merci-home" className="border border-white/20 text-white hover:border-gold hover:text-gold transition-colors px-8 py-4 text-xs uppercase tracking-[0.15em]">
                Retour à l'accueil
              </Link>
              <Link to="/journal" data-testid="merci-journal" className="bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-8 py-4 text-xs uppercase tracking-[0.15em] font-semibold inline-flex items-center justify-center gap-2">
                Lire le Journal Souverain <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        )}

        {state === "failed" && (
          <div data-testid="merci-failed">
            <h1 className="font-serif text-4xl text-white mb-4">Le paiement n'a pas abouti</h1>
            <p className="text-white/50 mb-8">Aucun montant n'a été prélevé. Vous pouvez réessayer.</p>
            <Link to="/services" className="bg-gold text-brand-ink px-8 py-4 text-xs uppercase tracking-[0.15em] font-semibold">
              Revenir aux parcours
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
